import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Clock, AlertCircle } from "lucide-react";
import SEOHelmet from "@/components/SEOHelmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactCTASection from "@/components/ContactCTASection";
import { useState } from "react";
import EstimateDialog from "@/components/EstimateDialog";

const SameDayOrganizing = () => {
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);

  const services = [
    "Emergency decluttering & organizing",
    "Pre-event home preparation",
    "Last-minute guest ready services",
    "Quick kitchen & pantry reset",
    "Rapid closet organization",
    "Move-in chaos management"
  ];

  return (
    <>
      <SEOHelmet
        title="Same-Day Organizing Southwest Michigan | Emergency Decluttering Near Me"
        description="Same-day emergency organizing in Southwest Michigan. Urgent decluttering, last-minute home prep, quick resets. Serving St. Joseph, Niles, Buchanan. Call for immediate service."
        keywords="same day organizing near me, emergency decluttering Southwest Michigan, last minute organizing Berrien County, urgent home organization St Joseph MI, same day organizer Niles Michigan, quick declutter Buchanan MI"
        canonicalUrl="https://organizebydesignmichigan.com/same-day-organizing"
      />

      <Header onBookingClick={() => setShowEstimateDialog(true)} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Clock className="h-5 w-5" />
              <span className="font-semibold">Available Today</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
              Same-Day Organizing Services in Michigan
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Need help NOW? Emergency organizing services for Michigan homes—available same-day. From last-minute guests to move-in chaos, we're here to help.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <Button onClick={() => setShowEstimateDialog(true)} size="lg" className="text-lg px-8">
                Book Same-Day Service
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8" asChild>
                <a href="tel:+16167270343">Call Now: 616 727-0343</a>
              </Button>
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <p>Subject to availability • Additional rush fees may apply</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-border">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl font-serif font-medium mb-3">
                  What We Can Do Today
                </CardTitle>
                <p className="text-muted-foreground">
                  Fast, effective organizing solutions for urgent situations
                </p>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid md:grid-cols-2 gap-4">
                  {services.map((service, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span>{service}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-primary/5 p-6 rounded-lg space-y-4">
                  <div>
                    <p className="text-lg font-semibold mb-2">Investment:</p>
                    <p className="text-muted-foreground">Starting at $150/hour (2-hour minimum)</p>
                    <p className="text-sm text-muted-foreground mt-1">Rush fees apply for same-day booking</p>
                  </div>
                  
                  <div>
                    <p className="text-lg font-semibold mb-2">Response Time:</p>
                    <p className="text-muted-foreground">We aim to arrive within 2-4 hours of booking (subject to availability)</p>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <Button onClick={() => setShowEstimateDialog(true)} size="lg" className="px-8">
                    Request Same-Day Service
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <ContactCTASection onBookingClick={() => setShowEstimateDialog(true)} />
      <Footer onBookingClick={() => setShowEstimateDialog(true)} />
      
      <EstimateDialog 
        open={showEstimateDialog} 
        onOpenChange={setShowEstimateDialog}
      />
    </>
  );
};

export default SameDayOrganizing;
